/**
 * Storage page — per docs/specs/storage.md
 *
 * R2 buckets, KV namespaces, Dropbox tokens, per-user quotas.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v5
 */
import { useState } from 'react';
import { Chip } from '../components/chip';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import {
    exportDropboxTokenList,
    useDropboxTokenHealth,
    useKvNamespaces,
    useR2Buckets,
    useStorageOrphans,
    useStorageKpis,
    useTopStorageUsers
} from '../../lib/api';
import { exportCsv } from '../../lib/csv';
import { getHttpErrorMessage } from '../../lib/http';
import { StorageRefreshModal } from './storage-refresh-modal';
import { StoragePurgeModal } from './storage-purge-modal';
import { bandwidthValue, bandwidthWindow } from './infra/r2-bandwidth';

const fmtMoney = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export function StoragePage() {
    const kpis = useStorageKpis();
    const r2 = useR2Buckets();
    const kv = useKvNamespaces();
    const dropbox = useDropboxTokenHealth();
    const top = useTopStorageUsers();
    const orphans = useStorageOrphans();
    const [refreshOpen, setRefreshOpen] = useState(false);
    const [purgeOpen, setPurgeOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const { toast } = useToast();

    const handleExportTokens = async () => {
        setExporting(true);
        try {
            const rows = await exportDropboxTokenList();
            const exportRows = rows.map((row) => ({
                email: row.email,
                status: row.status,
                expires_at: row.expires_at,
                refresh_failed: row.refresh_failed,
                has_refresh_token: row.has_refresh_token,
                provider_account_id: row.provider_account_id,
                account_type: row.account_type,
                connected_at: row.connected_at
            }));
            exportCsv(`dropbox-token-health-${new Date().toISOString().slice(0, 10)}`, exportRows, [
                'email',
                'status',
                'expires_at',
                'refresh_failed',
                'has_refresh_token',
                'provider_account_id',
                'account_type',
                'connected_at'
            ]);
            toast('Dropbox token export downloaded', {
                kind: 'info',
                detail: `${rows.length} row${rows.length === 1 ? '' : 's'}`
            });
        } catch (error) {
            toast('Export failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Token list could not be exported')
            });
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Storage</h1>
                    <p>R2 buckets, KV namespaces, Dropbox tokens, per-user quotas.</p>
                </div>
                <div className="page-actions">
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        onClick={() => setRefreshOpen(true)}
                    >
                        Refresh expired tokens
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={handleExportTokens}
                        disabled={exporting}
                    >
                        {exporting ? 'Exporting…' : 'Export token list'}
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={() => setPurgeOpen(true)}
                    >
                        Purge orphans
                    </RoleButton>
                </div>
            </header>

            <section className="kpi-grid kpi-grid-5" aria-label="Storage metrics">
                <KpiMini label="Total R2" value={`${kpis.data?.total_tb.toFixed(1) ?? '—'} TB`} />
                <KpiMini
                    label="Monthly cost"
                    value={kpis.data ? fmtMoney(kpis.data.monthly_cost_projection) : '—'}
                />
                <KpiMini label="KV keys" value={kpis.data?.kv_keys.toLocaleString() ?? '—'} />
                <KpiMini
                    label="Dropbox tokens"
                    value={kpis.data?.dropbox_tokens.toLocaleString() ?? '—'}
                />
                <KpiMini
                    label="R2 downloaded (30d)"
                    value={
                        kpis.isPending
                            ? 'Loading…'
                            : bandwidthValue(kpis.data?.bandwidth, 'downloaded_bytes')
                    }
                />
            </section>

            {!kpis.isPending &&
                (!kpis.data?.bandwidth || kpis.data.bandwidth.status === 'unavailable') && (
                    <section className="alert-bar">
                        <div className="alert" data-severity="warning">
                            <span className="msg">
                                R2 transfer measurements are unavailable. Refresh later to check
                                provider access.
                            </span>
                            <span className="meta">source unavailable</span>
                        </div>
                    </section>
                )}

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">R2 buckets</h2>
                </header>
                <p className="text-muted">
                    Adaptive transfer measurements exclude transfers smaller than 100 KiB. Shared
                    buckets include traffic from both environments. These values are not billing
                    totals. UTC window: {bandwidthWindow(kpis.data?.bandwidth)}. Observed:{' '}
                    {kpis.data?.bandwidth?.observed_at || 'unavailable'}.
                </p>
                {r2.data && r2.data.length > 0 ? (
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Bucket</th>
                                    <th className="numeric">Size</th>
                                    <th className="numeric">Objects</th>
                                    <th className="numeric">Monthly cost</th>
                                    <th className="numeric">Uploaded (30d)</th>
                                    <th className="numeric">Downloaded (30d)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {r2.data.map((b) => (
                                    <tr key={b.name}>
                                        <td className="mono">{b.name}</td>
                                        <td className="numeric">
                                            {b.size_gb < 1000
                                                ? `${b.size_gb.toFixed(1)} GB`
                                                : `${(b.size_gb / 1000).toFixed(1)} TB`}
                                        </td>
                                        <td className="numeric text-muted">
                                            {b.object_count.toLocaleString()}
                                        </td>
                                        <td className="numeric">
                                            {fmtMoney(b.monthly_cost_projection)}
                                        </td>
                                        <td className="numeric">
                                            {bandwidthValue(b.bandwidth, 'uploaded_bytes')}
                                        </td>
                                        <td className="numeric">
                                            {bandwidthValue(b.bandwidth, 'downloaded_bytes')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="title">No R2 buckets available on this worker</div>
                    </div>
                )}
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">KV namespaces</h2>
                        <Chip tone={kpis.data?.kv_size_available === false ? 'warning' : 'success'}>
                            {kpis.data?.kv_size_available === false ? 'size unavailable' : 'live'}
                        </Chip>
                    </header>
                    {kv.data && kv.data.length > 0 ? (
                        <div className="table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Namespace</th>
                                        <th className="numeric">Size</th>
                                        <th className="numeric">Keys</th>
                                        <th className="numeric">Reads/min</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kv.data.map((k) => (
                                        <tr key={k.name}>
                                            <td>
                                                <div className="user-meta">
                                                    <span className="user-name mono">{k.name}</span>
                                                    <span className="user-sub">{k.purpose}</span>
                                                </div>
                                            </td>
                                            <td className="numeric">
                                                {k.size_mb_available === false
                                                    ? 'unavailable'
                                                    : `${k.size_mb.toFixed(1)} MB`}
                                            </td>
                                            <td className="numeric text-muted">
                                                {k.key_count.toLocaleString()}
                                            </td>
                                            <td className="numeric text-muted">
                                                {k.reads_per_min_available === false
                                                    ? 'unavailable'
                                                    : k.reads_per_min.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="title">No KV namespaces are bound here</div>
                        </div>
                    )}
                    {kpis.data?.kv_size_available === false && (
                        <div className="card-body" style={{ paddingTop: 0 }}>
                            <div className="text-muted" style={{ fontSize: 11 }}>
                                KV byte size and reads/min are not persisted server-side yet.
                            </div>
                        </div>
                    )}
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Dropbox token health</h2>
                    </header>
                    <div className="card-body">
                        {dropbox.data && (
                            <dl className="kv-list">
                                <dt>Valid</dt>
                                <dd className="mono text-success">
                                    {dropbox.data.valid.toLocaleString()}
                                </dd>
                                <dt>Expiring &lt; 24h</dt>
                                <dd className="mono text-warning">{dropbox.data.expiring_24h}</dd>
                                <dt>Expired / refused</dt>
                                <dd className="mono text-danger">{dropbox.data.expired}</dd>
                                <dt>Auto-refresh success</dt>
                                <dd className="mono">
                                    {dropbox.data.auto_refresh_success_pct.toFixed(1)}%
                                </dd>
                            </dl>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Top users by storage</h2>
                </header>
                <div className="card-body tight">
                    {top.data && top.data.length > 0 ? (
                        top.data.map((u) => {
                            const pct = (u.storage_gb / Math.max(1, u.limit_gb)) * 100;
                            const tone = pct >= 100 ? 'danger' : pct > 80 ? 'warning' : undefined;
                            return (
                                <div
                                    key={u.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 200px auto',
                                        gap: 12,
                                        alignItems: 'center',
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ color: 'var(--text-primary)' }}>{u.email}</div>
                                    <div className="progress" style={{ gap: 2 }}>
                                        <div className="progress-track">
                                            <div
                                                className="progress-fill"
                                                data-tone={tone}
                                                style={{ width: `${Math.min(100, pct)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span
                                        className="mono"
                                        style={{
                                            color: u.overage
                                                ? 'var(--danger)'
                                                : 'var(--text-primary)'
                                        }}
                                    >
                                        {u.storage_gb.toFixed(1)} / {u.limit_gb} GB
                                    </span>
                                </div>
                            );
                        })
                    ) : (
                        <div className="empty-state">
                            <div className="title">No storage usage has been attributed yet</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Orphan detection</h2>
                    {orphans.data && (
                        <Chip tone={orphans.data.objects > 0 ? 'danger' : 'success'}>
                            {orphans.data.objects > 0 ? 'cleanup recommended' : 'clean'}
                        </Chip>
                    )}
                </header>
                <div className="card-body">
                    {orphans.data ? (
                        orphans.data.objects > 0 ? (
                            <div className="stack-12">
                                <dl className="kv-list">
                                    <dt>Bucket</dt>
                                    <dd className="mono">{orphans.data.bucket}</dd>
                                    <dt>Objects</dt>
                                    <dd className="mono text-danger">
                                        {orphans.data.objects.toLocaleString()}
                                    </dd>
                                    <dt>Approx size</dt>
                                    <dd className="mono">{orphans.data.size_gb.toFixed(2)} GB</dd>
                                    <dt>Scanned</dt>
                                    <dd className="mono">
                                        {orphans.data.scanned.toLocaleString()}
                                    </dd>
                                    <dt>Referenced objects</dt>
                                    <dd className="mono text-muted">
                                        {orphans.data.referenced.toLocaleString()}
                                    </dd>
                                    <dt>Last scan</dt>
                                    <dd className="mono">
                                        {new Date(orphans.data.last_scan).toLocaleString()}
                                    </dd>
                                </dl>
                                {orphans.data.sample_keys.length > 0 && (
                                    <div className="form-field" style={{ marginBottom: 0 }}>
                                        <span className="form-label">Sample keys</span>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gap: 6,
                                                padding: 12,
                                                border: '1px solid var(--border)',
                                                borderRadius: 8,
                                                background:
                                                    'color-mix(in oklab, var(--surface-elev-2) 75%, transparent)'
                                            }}
                                        >
                                            {orphans.data.sample_keys.map((key) => (
                                                <code
                                                    key={key}
                                                    className="mono"
                                                    style={{
                                                        fontSize: 11,
                                                        color: 'var(--text-primary)'
                                                    }}
                                                >
                                                    {key}
                                                </code>
                                            ))}
                                        </div>
                                        {orphans.data.truncated && (
                                            <div className="form-hint">
                                                Delete scans stop after the first batch limit.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="title">No orphaned R2 objects detected</div>
                                <div className="text-muted">
                                    Latest dry-run scan covered{' '}
                                    {orphans.data.scanned.toLocaleString()} objects.
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="empty-state">
                            <div className="title">Running orphan scan…</div>
                        </div>
                    )}
                </div>
            </section>

            <StorageRefreshModal open={refreshOpen} onClose={() => setRefreshOpen(false)} />
            <StoragePurgeModal open={purgeOpen} onClose={() => setPurgeOpen(false)} />
        </div>
    );
}

function KpiMini({ label, value }: { label: string; value: string }) {
    return (
        <div className="kpi-tile">
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
